import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSignUpFlow, useAuthToken } from '@mystudioclass/auth';
import { Video, Eye, EyeOff, Building2 } from 'lucide-react';
import toast from 'react-hot-toast';

const API_URL = import.meta.env.VITE_API_URL;

export default function SignUpPage() {
  // Force light mode on auth pages
  useEffect(() => {
    document.documentElement.removeAttribute('data-theme');
  }, []);
  const { isLoaded, createSignUp, error } = useSignUpFlow();
  const { getToken } = useAuthToken();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phone: '',
    studioName: '',
    address: '',
    city: '',
    state: '',
    zipCode: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Phone number formatter
  const formatPhoneNumber = (value) => {
    const phoneNumber = value.replace(/\D/g, '');
    if (!phoneNumber) return '';
    const hasCountryCode = phoneNumber.length === 11 && phoneNumber[0] === '1';
    const digits = hasCountryCode ? phoneNumber.slice(1) : phoneNumber;
    if (digits.length < 4) {
      return hasCountryCode ? `+1 (${digits}` : `(${digits}`;
    } else if (digits.length < 7) {
      return hasCountryCode
        ? `+1 (${digits.slice(0, 3)}) ${digits.slice(3)}`
        : `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      return hasCountryCode
        ? `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`
        : `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData({ ...formData, phone: formatted });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isLoaded) return;

    setLoading(true);

    try {
      // Create user with Better Auth
      await createSignUp({
        emailAddress: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName
      });

      // Wait for session to be ready
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Get token for API calls
      let token = null;
      for (let i = 0; i < 5; i++) {
        try {
          token = await getToken();
          if (token) break;
        } catch (e) {
          console.log('Token attempt failed:', e);
        }
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      if (!token) {
        // Token might be null if using cookie-based auth - that's OK
        console.log('No bearer token - using cookie-based auth');
      }

      // Create user and studio in our database
      const apiUrl = API_URL || 'https://studioscale-backend-production.up.railway.app';
      console.log('Calling API:', `${apiUrl}/profile/register-business`);

      try {
        const headers = {
          'Content-Type': 'application/json'
        };

        // Include token if available
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetch(`${apiUrl}/profile/register-business`, {
          method: 'POST',
          headers,
          credentials: 'include', // Include cookies for session auth
          body: JSON.stringify({
            firstName: formData.firstName,
            lastName: formData.lastName,
            phone: formData.phone,
            studioName: formData.studioName,
            address: formData.address,
            city: formData.city,
            state: formData.state,
            zipCode: formData.zipCode
          })
        });

        const data = await response.json();
        console.log('API response:', response.status, data);

        if (!response.ok) {
          console.error('Failed to create business:', data);
          toast.error(data.message || 'Failed to create studio. Please try again.');
          return;
        }

        toast.success('Account created successfully!');
        // Use hard redirect to ensure fresh auth state on dashboard
        window.location.href = '/dashboard';
      } catch (fetchError) {
        console.error('Fetch error:', fetchError);
        toast.error('Network error. Please try again.');
        return;
      }
    } catch (err) {
      console.error('Sign up error:', err);
      const errorMessage = err.message || error?.message || 'Failed to create account';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" style={{ background: 'var(--bg-base)' }}>
      <div className="max-w-2xl w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <a href="https://mystudioclass.com" className="inline-block">
            <div
              className="inline-flex items-center justify-center w-16 h-16 rounded-xl mb-4 transition-all hover:-translate-y-0.5"
              style={{ background: 'linear-gradient(135deg, var(--primary-hover), var(--primary))' }}
            >
              <Video className="w-8 h-8" style={{ color: 'var(--bg-base)' }} />
            </div>
          </a>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>My Studio Class</h1>
          <p className="mt-2" style={{ color: 'var(--text-muted)' }}>Business Portal</p>
        </div>

        {/* Registration Form */}
        <div className="rounded-2xl shadow-xl p-8" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}>
          <div className="flex items-center justify-center mb-6 p-3 rounded-lg" style={{ background: 'var(--primary-10)', border: '1px solid var(--primary-20)' }}>
            <Building2 className="w-5 h-5 mr-2" style={{ color: 'var(--primary)' }} />
            <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Business Sign Up</span>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Admin Account Details */}
            <div>
              <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Admin Account Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">First Name</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="label">Last Name</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className="input"
                    required
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="label">Studio Admin Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="input"
                  placeholder="admin@yourstudio.com"
                  required
                />
                <p className="mt-1.5 text-sm" style={{ color: 'var(--text-muted)' }}>
                  This will be your studio's primary admin login. You can share this with your team.
                </p>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className="input pr-10"
                      minLength="8"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                      style={{ color: 'var(--text-muted)' }}
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="label">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    placeholder="(555) 123-4567"
                    maxLength={17}
                    className="input"
                  />
                </div>
              </div>
            </div>

            {/* Studio Information */}
            <div className="pt-6" style={{ borderTop: '1px solid var(--border-subtle)' }}>
              <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Studio Information</h3>

              <div>
                <label className="label">Studio Name</label>
                <input
                  type="text"
                  name="studioName"
                  value={formData.studioName}
                  onChange={handleChange}
                  className="input"
                  placeholder="Your studio's business name"
                  required
                />
                <p className="mt-1.5 text-sm" style={{ color: 'var(--text-muted)' }}>
                  Your studio's business name
                </p>
              </div>

              <div className="mt-4">
                <label className="label">Address</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="input"
                />
              </div>

              <div className="mt-4 grid grid-cols-3 gap-4">
                <div className="col-span-2">
                  <label className="label">City</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="input"
                  />
                </div>
                <div>
                  <label className="label">State</label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    className="input"
                    maxLength="2"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="label">Zip Code</label>
                <input
                  type="text"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleChange}
                  className="input"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !isLoaded}
              className="btn btn-primary w-full"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              Already have an account?{' '}
              <Link to="/sign-in" className="font-medium transition-colors" style={{ color: 'var(--primary)' }}>
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
